import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import {
  getOrganizationAgentRadar,
  getOrganizationBlastRadius,
  getOrganizationRiskFlow,
} from '@kirin/api-client/api/ai-risk-map';
import type {
  AgentRadarResponse,
  BlastRadiusResponse,
  RiskFlowDataset,
  RiskFlowResponse,
} from '@kirin/api-client/contracts/ai-risk-map';

import { apiClient } from '@/lib/api-client';
import { queryKeys } from './queryKeys';
import { cacheConfigForCollaborativeData } from './queryConfig';
import { retryHandlers } from './helpers';

/** How often the Agent Radar refreshes its live snapshot. */
export const AGENT_RADAR_POLL_MS = 15_000;

/**
 * Longest window the Agent Radar asks for. The server caps a Risk Map range
 * (400 days); the radar's `to` is "now" at fetch time, so a 12-month view (or
 * a tab left open for days) would otherwise drift past the cap.
 */
export const AGENT_RADAR_MAX_RANGE_MS = 365 * 24 * 60 * 60 * 1000;

/**
 * `from`, moved forward so `[from, now]` spans at most the radar's max range.
 * The radar fetches with this (re-clamped at each poll) and labels its window
 * with it, so the "since" label matches what is shown.
 */
export function clampRadarFrom(from: string, now: number): string {
  const earliest = now - AGENT_RADAR_MAX_RANGE_MS;
  return Date.parse(from) >= earliest ? from : new Date(earliest).toISOString();
}

interface RiskMapRange {
  from: string;
  to: string;
}

const MINUTE_MS = 60_000;

/**
 * The window widened to whole minutes (`from` floored, `to` ceiled), as ISO
 * strings. The time-range presets are computed from `Date.now()` with ms
 * precision; quantising makes reloads (and other admins) within the same
 * minute produce the same query key and the same server cache key.
 */
export function quantiseRiskMapRange(from: Date, to: Date): RiskMapRange {
  return {
    from: new Date(
      Math.floor(from.getTime() / MINUTE_MS) * MINUTE_MS
    ).toISOString(),
    to: new Date(Math.ceil(to.getTime() / MINUTE_MS) * MINUTE_MS).toISOString(),
  };
}

/** KIR-5075: Risk Flow (alerts or inventory) for the AI Risk Map. */
export function useRiskFlow(
  organizationId: string | undefined,
  range: RiskMapRange,
  dataset: RiskFlowDataset,
  enabled = true
): UseQueryResult<RiskFlowResponse, Error> {
  return useQuery<RiskFlowResponse, Error>({
    queryKey: queryKeys.organizations.riskMapFlow(organizationId ?? '', {
      ...range,
      dataset,
    }),
    queryFn: () => {
      if (!organizationId) throw new Error('organizationId is required');
      return getOrganizationRiskFlow(apiClient, organizationId, {
        ...range,
        dataset,
      });
    },
    enabled: enabled && !!organizationId,
    ...cacheConfigForCollaborativeData,
    retry: retryHandlers.list,
  });
}

/** KIR-5075: Blast Radius graph for the AI Risk Map. */
export function useBlastRadius(
  organizationId: string | undefined,
  range: RiskMapRange,
  enabled = true
): UseQueryResult<BlastRadiusResponse, Error> {
  return useQuery<BlastRadiusResponse, Error>({
    queryKey: queryKeys.organizations.riskMapBlastRadius(
      organizationId ?? '',
      range
    ),
    queryFn: () => {
      if (!organizationId) throw new Error('organizationId is required');
      return getOrganizationBlastRadius(apiClient, organizationId, range);
    },
    enabled: enabled && !!organizationId,
    ...cacheConfigForCollaborativeData,
    retry: retryHandlers.list,
  });
}

/**
 * KIR-5075: Agent Radar, live. Polls every {@link AGENT_RADAR_POLL_MS}; `to`
 * is "now" at fetch time so new activity keeps arriving while the tab is open.
 * The query key holds only `from` so polling refreshes one cache entry.
 * `from` is clamped to at most {@link AGENT_RADAR_MAX_RANGE_MS} before now.
 */
export function useAgentRadar(
  organizationId: string | undefined,
  from: string,
  enabled = true
): UseQueryResult<AgentRadarResponse, Error> {
  return useQuery<AgentRadarResponse, Error>({
    queryKey: queryKeys.organizations.riskMapRadar(organizationId ?? '', {
      from,
    }),
    queryFn: () => {
      if (!organizationId) throw new Error('organizationId is required');
      // Clamped here, at fetch time, so every poll stays within the cap.
      const now = Date.now();
      return getOrganizationAgentRadar(apiClient, organizationId, {
        from: clampRadarFrom(from, now),
        to: new Date(now).toISOString(),
      });
    },
    enabled: enabled && !!organizationId,
    refetchInterval: AGENT_RADAR_POLL_MS,
    refetchIntervalInBackground: false,
    retry: retryHandlers.list,
  });
}
